import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, X } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../../ui/popover';

const PRESETS = [5, 15, 30, 60, 120, 1440];

// "15m", "1h", "1d" in the app's language ("15 min", "1 h", "1 d" in Slovak).
const formatOffset = (minutes, lang) => {
  const [amount, unit] = minutes % 1440 === 0 ? [minutes / 1440, 'day']
    : minutes % 60 === 0 ? [minutes / 60, 'hour'] : [minutes, 'minute'];
  try {
    return new Intl.NumberFormat(lang, { style: 'unit', unit, unitDisplay: 'narrow' }).format(amount);
  } catch {
    return `${amount}${unit[0]}`;
  }
};

/**
 * Reminders before the task's time (the backend sends them: utils/task_reminders.py).
 * hasTime: whether the task has a time to be reminded before; without one the picker
 * says so (the reminders wait until it gets one).
 */
const ReminderPicker = ({ value = [], onChange, children, disabled = false, hasTime = true }) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const when = (r) => (r.minutes_before != null
    ? formatOffset(r.minutes_before, i18n.language)
    : new Date(r.date).toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' }));

  const addReminder = (minutes) => {
    const newReminder = {
      id: Date.now().toString(),
      type: 'before_task',
      minutes_before: minutes,
      notified: false,
    };
    onChange([...value, newReminder]);
  };

  const removeReminder = (id) => {
    onChange(value.filter(r => r.id !== id));
  };

  const defaultTrigger = (
    // type="button": inside a form (a habit's) it opens the picker, never sends the form
    <button type="button" disabled={disabled} className={`flex items-center gap-1.5 px-2 py-1 rounded-md text-[13px] transition-colors ${
      disabled ? 'opacity-50 cursor-not-allowed' :
      value.length > 0 ? 'text-blue-400 bg-blue-500/10' : 'text-muted-foreground hover:bg-accent/60'
    }`}>
      <Bell className="w-3.5 h-3.5" />
      <span>{value.length > 0 ? t('tasks.reminders_before', { time: value.map(when).join(', ') }) : t('tasks.reminders_add')}</span>
    </button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {children || defaultTrigger}
      </PopoverTrigger>
      <PopoverContent className="w-52 p-3 bg-accent border-border max-md:!w-screen max-md:!max-w-none max-md:!rounded-b-none" align="start" sideOffset={4}>
        {!hasTime && <p className="mb-2.5 text-xs text-muted-foreground">{t('tasks.reminders_needs_time')}</p>}
        {value.length > 0 && (
          <div className="mb-2.5 space-y-1">
            {value.map(r => (
              <div key={r.id} className="flex items-center justify-between px-2 py-1 bg-accent/60 rounded-md text-xs">
                <span className="text-foreground/80">{r.minutes_before != null ? t('tasks.reminders_before', { time: when(r) }) : when(r)}</span>
                <button type="button" onClick={() => removeReminder(r.id)} className="text-muted-foreground hover:text-red-400">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="grid grid-cols-3 gap-1.5">
          {PRESETS.map(minutes => (
            <button
              type="button"
              key={minutes}
              onClick={() => addReminder(minutes)}
              className="px-2 py-1.5 bg-accent/60 border border-border rounded-md text-xs text-foreground/80 hover:bg-accent"
            >
              {formatOffset(minutes, i18n.language)}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ReminderPicker;
